import React, { useState, useRef, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import Icon from 'components/AppIcon';


const MARKETS = [
  { id: 'oma', label: 'Omaha Metro', region: 'Midwest', mlsId: 'GPRMLS', coverage: '98%', compliant: true },
  { id: 'kc', label: 'Kansas City Metro', region: 'Midwest', mlsId: 'HMLS', coverage: '96%', compliant: true },
  { id: 'des', label: 'Des Moines Metro', region: 'Midwest', mlsId: 'DMAAR', coverage: '94%', compliant: true },
  { id: 'lin', label: 'Lincoln, NE', region: 'Midwest', mlsId: 'LNKMLS', coverage: '97%', compliant: true },
  { id: 'stl', label: 'St. Louis Metro', region: 'Midwest', mlsId: 'MARIS', coverage: '95%', compliant: true },
  { id: 'min', label: 'Minneapolis Metro', region: 'Midwest', mlsId: 'NorthstarMLS', coverage: '99%', compliant: true },
];

const NAV_TABS = [
  {
    label: 'Property Analysis',
    path: '/property-comparison-hub',
    icon: 'BarChart2',
    tooltip: 'CMA workspace for comparable property identification and side-by-side valuation analysis',
  },
  {
    label: 'Market Intelligence',
    path: '/market-trends-analytics',
    icon: 'TrendingUp',
    tooltip: 'Market trends, pricing patterns, and neighborhood analytics for contextual decision support',
  },
  {
    label: 'Investment Analysis',
    path: '/investment-roi-dashboard',
    icon: 'DollarSign',
    tooltip: 'ROI calculations, cash flow projections, and investment opportunity ranking',
  },
];

export default function MarketSelectorHeader({ onMarketChange, onTabChange }) {
  const navigate = useNavigate();
  const location = useLocation();

  const [selectedMarket, setSelectedMarket] = useState(MARKETS?.[0]);
  const [marketOpen, setMarketOpen] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const marketRef = useRef(null);

  const activeTab = NAV_TABS?.find(t => t?.path === location?.pathname) || NAV_TABS?.[0];

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (marketRef?.current && !marketRef?.current?.contains(e?.target)) {
        setMarketOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleMarketSelect = (market) => {
    setSelectedMarket(market);
    setMarketOpen(false);
    onMarketChange?.(market);
  };

  const handleTabClick = (tab) => {
    navigate(tab?.path);
    setMobileMenuOpen(false);
    onTabChange?.(tab);
  };

  const handleKeyDown = (e, tab) => {
    if (e?.key === 'Enter' || e?.key === ' ') {
      e?.preventDefault();
      handleTabClick(tab);
    }
  };

  return (
    <>
      {/* Fixed Header */}
      <header className="market-header">
        <div className="market-header-inner">
          {/* Brand */}
          <div className="market-header-brand">
            <div className="market-header-logo">
              <Icon name="Home" size={18} color="currentColor" />
            </div>
            <div className="hidden sm:block">
              <span className="font-heading font-700 text-base text-foreground tracking-tight">
                CMA Pro
              </span>
              <span className="ml-2 text-xs font-caption text-muted-foreground bg-muted px-1.5 py-0.5 rounded">
                MLS Certified
              </span>
            </div>
          </div>

          {/* Center: Market Selector */}
          <div className="relative" ref={marketRef}>
            <button
              onClick={() => setMarketOpen(prev => !prev)}
              className="flex items-center gap-2 px-3 py-2 rounded-lg border border-border bg-background
                         hover:bg-muted transition-standard text-sm font-caption font-medium text-foreground
                         focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
              aria-haspopup="listbox"
              aria-expanded={marketOpen}
            >
              <Icon name="MapPin" size={14} color="var(--color-primary)" />
              <span className="hidden sm:inline max-w-[160px] truncate">{selectedMarket?.label}</span>
              <span className="sm:hidden text-xs">{selectedMarket?.id?.toUpperCase()}</span>
              <Icon name="ChevronDown" size={14} color="currentColor"
                className={`transition-transform duration-200 ${marketOpen ? 'rotate-180' : ''}`} />
            </button>

            {marketOpen && (
              <div className="export-dropdown w-72" role="listbox" aria-label="Select market">
                <div className="px-4 py-2 border-b border-border">
                  <p className="text-xs font-caption font-medium text-muted-foreground uppercase tracking-wide">
                    MLS Markets
                  </p>
                </div>
                {MARKETS?.map((market) => (
                  <button
                    key={market?.id}
                    role="option"
                    aria-selected={selectedMarket?.id === market?.id}
                    onClick={() => handleMarketSelect(market)}
                    className={`export-option w-full text-left ${selectedMarket?.id === market?.id ? 'bg-muted text-primary' : ''}`}
                  >
                    <div className="flex items-center justify-between w-full">
                      <div>
                        <p className="font-medium text-sm">{market?.label}</p>
                        <p className="text-xs text-muted-foreground">{market?.mlsId} · {market?.coverage} coverage</p>
                      </div>
                      <div className="flex items-center gap-1.5">
                        {market?.compliant && (
                          <Icon name="ShieldCheck" size={14} color="var(--color-success)" />
                        )}
                        {selectedMarket?.id === market?.id && (
                          <Icon name="Check" size={14} color="var(--color-primary)" />
                        )}
                      </div>
                    </div>
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Right: Badges + Mobile Toggle */}
          <div className="flex items-center gap-3">
            <div className="hidden md:flex items-center gap-2">
              <span className="text-xs font-caption text-muted-foreground bg-muted px-2 py-1 rounded border border-border">
                NAR Compliant
              </span>
              <span className="text-xs font-caption text-success bg-success/10 px-2 py-1 rounded border border-success/20">
                REALTOR®
              </span>
            </div>

            {/* Mobile hamburger for tabs */}
            <button
              className="lg:hidden flex items-center justify-center w-9 h-9 rounded-lg
                         hover:bg-muted transition-standard focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
              onClick={() => setMobileMenuOpen(prev => !prev)}
              aria-label="Toggle navigation menu"
              aria-expanded={mobileMenuOpen}
            >
              <Icon name={mobileMenuOpen ? 'X' : 'Menu'} size={18} color="currentColor" />
            </button>
          </div>
        </div>
      </header>
      {/* Desktop Tab Navigation */}
      <nav
        className="nav-tab-bar hidden lg:block"
        style={{ top: '60px' }}
        aria-label="Primary navigation"
      >
        <div className="flex items-center px-6 max-w-screen-2xl mx-auto">
          {NAV_TABS?.map((tab) => {
            const isActive = location?.pathname === tab?.path;
            return (
              <button
                key={tab?.path}
                role="tab"
                aria-selected={isActive}
                aria-label={tab?.tooltip}
                title={tab?.tooltip}
                onClick={() => handleTabClick(tab)}
                onKeyDown={(e) => handleKeyDown(e, tab)}
                className={`nav-tab-item ${isActive ? 'active' : ''}`}
              >
                <Icon name={tab?.icon} size={16} color="currentColor" />
                <span>{tab?.label}</span>
              </button>
            );
          })}
        </div>
      </nav>
      {/* Mobile Dropdown Nav Menu */}
      {mobileMenuOpen && (
        <div
          className="lg:hidden fixed left-0 right-0 z-[998] bg-card border-b border-border"
          style={{ top: '60px' }}
        >
          {NAV_TABS?.map((tab) => {
            const isActive = location?.pathname === tab?.path;
            return (
              <button
                key={tab?.path}
                onClick={() => handleTabClick(tab)}
                className={`flex items-center gap-3 w-full px-6 py-4 text-sm font-caption font-medium
                            border-b border-border transition-standard
                            ${isActive ? 'text-primary bg-muted' : 'text-foreground hover:bg-muted'}`}
              >
                <Icon name={tab?.icon} size={18} color="currentColor" />
                <span>{tab?.label}</span>
                {isActive && <Icon name="ChevronRight" size={14} color="currentColor" className="ml-auto" />}
              </button>
            );
          })}
        </div>
      )}
      {/* Mobile Bottom Tab Bar */}
      <nav className="mobile-tab-bar lg:hidden" aria-label="Mobile navigation">
        <div className="flex items-stretch h-full">
          {NAV_TABS?.map((tab) => {
            const isActive = location?.pathname === tab?.path;
            return (
              <button
                key={tab?.path}
                role="tab"
                aria-selected={isActive}
                onClick={() => handleTabClick(tab)}
                className={`mobile-tab-item ${isActive ? 'active' : ''}`}
              >
                <Icon name={tab?.icon} size={isActive ? 22 : 20} color="currentColor" />
                <span>{tab?.label?.split(' ')?.[0]}</span>
              </button>
            );
          })}
        </div>
      </nav>
    </>
  );
}